import React, {useMemo, useState} from 'react';
import ReactDOM from 'react-dom';

import Fuse from 'fuse.js';

import {
	Accordion,
	AccordionItem,
	AccordionItemButton,
	AccordionItemHeading,
	AccordionItemPanel,
} from 'react-accessible-accordion';

const SELECTOR_CONTAINER = '.js-bs-accordion';

let lastId = 0;

function uniqueId(prefix = 'js-bs-accordion-') {
	lastId++;
	return `${prefix}${lastId}`;
}

function AccordionApp({
	items = [],
	allowMultipleExpanded = false,
	allowZeroExpanded = true,
	searchable = false,
}) {
	const [componentId] = useState(() => uniqueId());
	const [searchTerm, setSearchTerm] = useState('');

	const fuse = useMemo(
		function createFuse() {
			return new Fuse(items, {
				sort: true,
				threshold: 0.5,
				distance: 9000,
				keys: ['name', 'heading', 'details'],
			});
		},
		[items]
	);

	let displayItems = items;
	if (searchTerm.length) {
		displayItems = fuse.search(searchTerm).map(({item}) => item);
	}

	return (
		<div id={componentId} className="bs-accordion">
			{searchable ? (
				<form
					className="bs-accordion-form"
					onReset={(_) => setSearchTerm('')}
					onSubmit={(e) => e.preventDefault()}
				>
					<fieldset className="bs-accordion-form__fieldset">
						<legend className="visuallyhidden">
							In Liste suchen
						</legend>

						<p className="bs-accordion-form__field">
							<span className="bs-accordion-form__input-group">
								<label
									className="bs-accordion-form__label"
									htmlFor={`${componentId}__searchTerm`}
								>
									<span className="visuallyhidden">
										Suchbegriff:
									</span>

									<svg
										viewBox="0 0 40 40"
										xmlns="http://www.w3.org/2000/svg"
										width="28"
										height="28"
									>
										<path d="M24.11 21.68L29 26.553 27.548 28l-4.9-4.882a6.7 6.7 0 01-3.931 1.266c-3.71 0-6.717-2.997-6.717-6.693C12 13.996 15.008 11 18.717 11c3.708 0 6.715 2.996 6.715 6.691a6.648 6.648 0 01-1.322 3.989zm-.112-3.989c0-2.906-2.364-5.262-5.281-5.262-2.918 0-5.283 2.356-5.283 5.262 0 2.907 2.365 5.264 5.283 5.264 2.917 0 5.281-2.357 5.281-5.264z" />
									</svg>
									<input
										id={`${componentId}__searchTerm`}
										className="bs-accordion-form__input bs-accordion-form__input--search"
										onChange={(e) =>
											setSearchTerm(e.target.value)
										}
										placeholder="In Liste suchen …"
										autoComplete="off"
									/>
								</label>

								{searchTerm.length ? (
									<button
										className="bs-accordion-form__button bs-accordion-form__button--reset"
										type="reset"
										title="Suche zurücksetzen"
									>
										<span style={{speak: 'none'}}>×</span>
										<span className="visuallyhidden">
											Suche zurücksetzen
										</span>
									</button>
								) : null}
							</span>

							<span className="bs-accordion-form__result-count">
								Zeige {displayItems.length} / {items.length}{' '}
								{displayItems.length > 1
									? 'Einträge'
									: 'Eintrag'}
							</span>
						</p>
					</fieldset>
				</form>
			) : null}

			{displayItems.length ? (
				<Accordion
					allowMultipleExpanded={allowMultipleExpanded}
					allowZeroExpanded={allowZeroExpanded}
					className="bs-accordion-accordion"
				>
					{displayItems.map(({id, details, name, heading = null}) => (
						<AccordionItem
							key={id}
							className="bs-accordion-accordion__item"
						>
							<AccordionItemHeading className="bs-accordion-accordion__heading">
								<AccordionItemButton className="bs-accordion-accordion__button">
									{heading ? (
										<div
											className="bs-accordion-accordion__button__content-block"
											dangerouslySetInnerHTML={{
												__html: heading,
											}}
										/>
									) : (
										name
									)}
								</AccordionItemButton>
							</AccordionItemHeading>
							<AccordionItemPanel className="bs-accordion-accordion__panel">
								<div
									dangerouslySetInnerHTML={{__html: details}}
								/>
							</AccordionItemPanel>
						</AccordionItem>
					))}
				</Accordion>
			) : (
				<p className="bs-accordion-message">Keine Einträge gefunden</p>
			)}
		</div>
	);
}

function createInstance(containerElement) {
	if (!containerElement.matches('[data-bs-accordion-initialized="true"]')) {
		containerElement.setAttribute('data-bs-accordion-initialized', 'true');

		const data = JSON.parse(
			containerElement.getAttribute('data-bs-accordion')
		);
		ReactDOM.render(
			<AccordionApp
				items={data.items}
				allowMultipleExpanded={!!data.allowMultipleExpanded}
				allowZeroExpanded={!!data.allowZeroExpanded}
				searchable={!!data.searchable}
			/>,
			containerElement
		);
	}
}

export function render() {
	[...document.querySelectorAll(SELECTOR_CONTAINER)].forEach(createInstance);
}
